import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  TrendingUp,
  PoundSterling,
  Users,
  ChevronDown,
  Play,
  Wrench,
  Download,
  Target,
  Filter,
} from 'lucide-react';
import { MetricCard, PercentageBadge, WeightBar, LogEntry, formatCurrency } from './SharedBits';

export default function SalesTab({
  data,
  selectedBookingMonth,
  setSelectedBookingMonth,
  availableBookingMonths,
  deliveryDistribution,
  chartData,
  chartOptions,
  downloadCSV,
  diagnosticOpen,
  setDiagnosticOpen,
  runDiagnostic,
  loading,
  diagnosticLogs,
  clearLogs,
}) {
  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <MetricCard
          title="Won Deals"
          value={data?.summary?.totalWonDeals || 0}
          icon={Users}
          subtitle="Total bookings"
        />
        <MetricCard
          title="Total Value"
          value={formatCurrency(data?.summary?.totalBookingValue || 0)}
          icon={TrendingUp}
          subtitle="Full booking value"
        />
        <MetricCard
          title="Balance Due"
          value={formatCurrency(data?.summary?.totalBalanceDue || 0)}
          icon={PoundSterling}
          subtitle="50% of total value"
        />
      </div>

      {/* Deals Closed Per Month */}
      {data?.closedByMonth && data.closedByMonth.length > 0 && (
        <Card className="mb-8 bg-white border-stone-100 shadow-sm" data-testid="deals-closed-section">
          <CardHeader className="border-b border-stone-100 pb-4">
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Target className="w-5 h-5 text-gold-600" />
              Deals Closed Per Month (Last 12 Months)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {data.closedByMonth.map((month) => (
                <div
                  key={month.key}
                  className={`rounded-xl p-4 border cursor-pointer transition-all ${
                    selectedBookingMonth === month.key
                      ? 'bg-gold-100 border-gold-300 ring-2 ring-gold-400'
                      : month.isCurrentMonth
                        ? 'bg-gold-50 border-gold-200 hover:border-gold-300'
                        : 'bg-stone-50 border-stone-100 hover:border-stone-200'
                  } ${month.dealsCount === 0 ? 'opacity-50' : ''}`}
                  onClick={() => month.dealsCount > 0 && setSelectedBookingMonth(month.key)}
                  data-testid={`closed-month-${month.key}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${
                      selectedBookingMonth === month.key ? 'text-gold-800' :
                      month.isCurrentMonth ? 'text-gold-700' : 'text-stone-600'
                    }`}>
                      {month.shortLabel}
                    </span>
                    {month.isCurrentMonth && (
                      <span className="text-[10px] bg-gold-500 text-white px-1.5 py-0.5 rounded-full">Now</span>
                    )}
                  </div>
                  <p className={`text-2xl font-bold ${
                    selectedBookingMonth === month.key ? 'text-gold-800' :
                    month.isCurrentMonth ? 'text-gold-700' : 'text-stone-900'
                  }`}>
                    {month.dealsCount}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">deals closed</p>
                  <div className="mt-2 pt-2 border-t border-stone-200/50">
                    <p className="text-sm font-semibold text-stone-700">{formatCurrency(month.totalValue)}</p>
                    <p className="text-xs text-stone-400">Avg: {formatCurrency(month.avgPerSale)}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-stone-400 mt-4 text-center">
              Click a month to see when balance payments are due
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delivery Distribution for Selected Month */}
      {deliveryDistribution && (
        <>
          <Card className="mb-8 bg-white border-stone-100 shadow-sm" data-testid="delivery-distribution">
            <CardHeader className="border-b border-stone-100 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
                    <Filter className="w-5 h-5 text-gold-600" />
                    Balance Delivery Forecast
                  </CardTitle>
                  <p className="text-sm text-stone-500 mt-1">
                    When will <span className="font-semibold text-gold-700">{deliveryDistribution.bookingMonth}</span> bookings pay their 50% balance?
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedBookingMonth} onValueChange={setSelectedBookingMonth}>
                    <SelectTrigger className="w-[160px]" data-testid="month-selector">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBookingMonths.map(month => (
                        <SelectItem key={month.key} value={month.key}>
                          {month.fullLabel} ({month.dealsCount})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-stone-900">{deliveryDistribution.totalDeals}</p>
                  <p className="text-xs text-stone-500">Total Bookings</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gold-600">{formatCurrency(deliveryDistribution.totalValue)}</p>
                  <p className="text-xs text-stone-500">Booking Value</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-stone-900">{formatCurrency(deliveryDistribution.totalBalance)}</p>
                  <p className="text-xs text-stone-500">Balance Due (50%)</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-stone-900">{deliveryDistribution.dealsWithWeddingDate}</p>
                  <p className="text-xs text-stone-500">With Wedding Dates</p>
                </div>
              </div>

              {/* Chart */}
              {chartData && deliveryDistribution.deliveryMonths.length > 0 ? (
                <div className="h-[300px]">
                  <Bar data={chartData} options={chartOptions} />
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center">
                  <p className="text-stone-400">No delivery dates available for this booking month</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delivery Table */}
          {deliveryDistribution.deliveryMonths.length > 0 && (
            <Card className="mb-8 bg-white border-stone-100 shadow-sm overflow-hidden" data-testid="delivery-table">
              <CardHeader className="border-b border-stone-100 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium text-stone-800">
                    {deliveryDistribution.bookingMonth} Bookings - Balance Due By Month
                  </CardTitle>
                  <Button
                    onClick={downloadCSV}
                    variant="outline"
                    className="border-stone-200 hover:bg-stone-50"
                    data-testid="download-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
              </CardHeader>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-stone-500 font-medium text-xs uppercase tracking-wider py-3 px-4 text-left border-b border-stone-100">Delivery Month</th>
                      <th className="text-stone-500 font-medium text-xs uppercase tracking-wider py-3 px-4 text-right border-b border-stone-100">Bookings</th>
                      <th className="text-stone-500 font-medium text-xs uppercase tracking-wider py-3 px-4 text-right border-b border-stone-100">Balance Due</th>
                      <th className="text-stone-500 font-medium text-xs uppercase tracking-wider py-3 px-4 text-center border-b border-stone-100">% of Total</th>
                      <th className="text-stone-500 font-medium text-xs uppercase tracking-wider py-3 px-4 text-left border-b border-stone-100 hidden md:table-cell">Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveryDistribution.deliveryMonths.map((month) => (
                      <tr
                        key={month.key}
                        className="border-b border-stone-50 hover:bg-stone-50/50 transition-colors"
                        data-testid={`delivery-row-${month.key}`}
                      >
                        <td className="py-4 px-4 text-sm font-medium text-stone-700">
                          {month.fullLabel}
                        </td>
                        <td className="py-4 px-4 text-sm text-stone-700 text-right font-medium">
                          {month.dealsCount}
                        </td>
                        <td className="py-4 px-4 text-sm text-stone-900 text-right font-semibold">
                          {formatCurrency(month.balanceValue)}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <PercentageBadge value={month.percentOfTotal} />
                        </td>
                        <td className="py-4 px-4 hidden md:table-cell">
                          <WeightBar value={month.balanceValue} max={deliveryDistribution.maxBalance} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Diagnostic Panel */}
      <Collapsible open={diagnosticOpen} onOpenChange={setDiagnosticOpen}>
        <Card className="bg-white border-stone-100" data-testid="diagnostic-panel">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-stone-50 transition-colors border-b border-stone-100">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium text-stone-700 flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  Diagnostic Panel
                </CardTitle>
                <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform ${diagnosticOpen ? 'rotate-180' : ''}`} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Button
                  onClick={runDiagnostic}
                  disabled={loading}
                  variant="outline"
                  className="border-stone-200 hover:bg-stone-50"
                  data-testid="run-diagnostic-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run Diagnostic
                </Button>
                {diagnosticLogs.length > 0 && (
                  <Button
                    onClick={clearLogs}
                    variant="ghost"
                    className="text-stone-500 hover:text-stone-700"
                    data-testid="clear-logs-btn"
                  >
                    Clear Logs
                  </Button>
                )}
              </div>

              {diagnosticLogs.length > 0 ? (
                <div className="bg-stone-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  {diagnosticLogs.map((log, index) => (
                    <LogEntry key={index} log={log} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-stone-400 italic">
                  Click "Run Diagnostic" to test API endpoints and verify field names.
                </p>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </>
  );
}
